/**
 * Cumulative balance chart for the client dashboard.
 *
 * Two stacked areas: solid for historical, dashed/translucent for the
 * forward projection (if the client keeps fulfilling their active
 * commitments). They share the same gold→navy gradient so the eye
 * reads them as one continuous story, with the dash hinting "this part
 * hasn't happened yet."
 *
 * The big-number callout above the chart anchors the satisfaction:
 * "you have $X — and on this schedule you'll have $Y in 6 months."
 */
import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatMoney } from '../../lib/datetime';
import RiskDisclaimer from '../RiskDisclaimer';
import { colors } from '../../styles/theme';
import type { ChartPoint, TimeRange } from '../../services/client.dashboard.service';
import { readerLocale, t } from '../../i18n/language';


interface Props {
  series: ChartPoint[];
  currency: string;
  currentBalance: number;
}

const RANGE_LABELS: Record<TimeRange, string> = {
  '1M': '1M',
  '6M': '6M',
  '1Y': '1Y',
  ALL: 'All',
};

interface GrowthTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ChartPoint }>;
  label?: string | number;
  currency: string;
}

/** Tooltip body for the growth chart. Hoisted to module scope (passed
 *  the active `currency` as a prop) so it isn't re-created on every
 *  parent render. */
function GrowthTooltip({
  active,
  payload,
  label,
  currency,
}: Readonly<GrowthTooltipProps>): JSX.Element | null {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  const value = point.historical ?? point.projected ?? 0;
  const isProjected = point.historical === null;
  return (
    <div
      className="rounded-lg shadow-lg px-3 py-2 text-xs"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <p style={{ color: colors.muted }}>
        {new Date(label ?? '').toLocaleDateString(readerLocale(), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
      <p
        className="font-semibold text-sm mt-0.5"
        style={{ color: isProjected ? colors.muted : colors.ink }}
      >
        {formatMoney(value, currency)}
      </p>
      {isProjected && (
        <p className="text-xs mt-0.5" style={{ color: colors.muted }}>
          Projected
        </p>
      )}
    </div>
  );
}

/** Cutoff for the historical slice given the active range. Projection
 *  is unaffected — we always show what's coming. */
function rangeStart(range: TimeRange): Date | null {
  if (range === 'ALL') return null;
  const now = new Date();
  if (range === '1M') now.setMonth(now.getMonth() - 1);
  if (range === '6M') now.setMonth(now.getMonth() - 6);
  if (range === '1Y') now.setFullYear(now.getFullYear() - 1);
  return now;
}

export default function GrowthChart({
  series,
  currency,
  currentBalance,
}: Readonly<Props>): JSX.Element {
  const [range, setRange] = useState<TimeRange>('6M');

  const filtered = useMemo(() => {
    const cutoff = rangeStart(range);
    if (!cutoff) return series;
    const cutoffMs = cutoff.getTime();
    return series.filter((p) => {
      const t = Date.parse(p.date);
      // Keep all projected points (they're always future); only filter
      // historical ones by the cutoff.
      return p.projected !== null || t >= cutoffMs;
    });
  }, [series, range]);

  // Project endpoint — last point of the dashed segment, used in the
  // "on track to $X" callout.
  const projectionEnd = useMemo(() => {
    for (let i = series.length - 1; i >= 0; i -= 1) {
      if (series[i].projected !== null) return series[i];
    }
    return null;
  }, [series]);

  const todayISO = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {/* ─── Header: big number + projection hint + range pills ─── */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p
            className="text-xs font-medium uppercase tracking-wider mb-1"
            style={{ color: colors.muted }}
          >
            Your holdings
          </p>
          <p
            className="text-3xl font-bold leading-none"
            style={{ color: colors.ink }}
          >
            {formatMoney(currentBalance, currency)}
          </p>
          {projectionEnd?.projected != null && projectionEnd.projected > currentBalance && (
            <p className="text-xs mt-2" style={{ color: colors.muted }}>
              On track to{' '}
              <strong style={{ color: colors.success }}>
                {formatMoney(projectionEnd.projected, currency)}
              </strong>{' '}
              {t('by', 'date')} {new Date(projectionEnd.date).toLocaleDateString(readerLocale(), {
                month: 'long',
                year: 'numeric',
              })}{' '}
              if you keep your scheduled contributions.
            </p>
          )}
          {projectionEnd?.projected != null && projectionEnd.projected > currentBalance && (
            <RiskDisclaimer compact extra="Projection is illustrative and assumes your scheduled contributions continue as planned." />
          )}
        </div>

        <div
          className="inline-flex rounded-lg p-0.5"
          style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
        >
          {(Object.keys(RANGE_LABELS) as TimeRange[]).map((r) => {
            const active = r === range;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className="px-3 py-1 text-xs font-medium rounded-md transition-colors"
                style={{
                  background: active ? colors.surface : 'transparent',
                  color: active ? colors.ink : colors.muted,
                  border: active ? `1px solid ${colors.border}` : '1px solid transparent',
                }}
              >
                {RANGE_LABELS[r]}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Chart ────────────────────────────────────────────── */}
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={filtered}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <defs>
            {/* Single gradient reused by both areas. Historical uses
                full opacity; projection drops to a soft 25%. */}
            <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.gold} stopOpacity={0.55} />
              <stop offset="100%" stopColor={colors.gold} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="growthFillProjected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.gold} stopOpacity={0.2} />
              <stop offset="100%" stopColor={colors.gold} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke={colors.borderSoft} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: colors.muted }}
            tickLine={false}
            axisLine={{ stroke: colors.border }}
            tickFormatter={(iso: string) =>
              new Date(iso).toLocaleDateString(readerLocale(), { month: 'short', day: 'numeric' })
            }
            minTickGap={32}
          />
          <YAxis
            tick={{ fontSize: 11, fill: colors.muted }}
            tickLine={false}
            axisLine={false}
            width={70}
            tickFormatter={(v: number) =>
              v >= 1000
                ? `${(v / 1000).toLocaleString(readerLocale(), { maximumFractionDigits: 1 })}k`
                : v.toFixed(0)
            }
          />
          <Tooltip content={<GrowthTooltip currency={currency} />} />

          {/* "Today" anchor line — separates real from projected. */}
          <ReferenceLine
            x={todayISO}
            stroke={colors.muted}
            strokeDasharray="2 4"
            strokeOpacity={0.5}
          />

          {/* Historical — solid stroke, full gradient. */}
          <Area
            type="monotone"
            dataKey="historical"
            stroke={colors.gold}
            strokeWidth={2.5}
            fill="url(#growthFill)"
            connectNulls={false}
            isAnimationActive
            animationDuration={600}
          />

          {/* Projected — dashed stroke, faded gradient. */}
          <Area
            type="monotone"
            dataKey="projected"
            stroke={colors.gold}
            strokeWidth={2}
            strokeDasharray="6 4"
            strokeOpacity={0.7}
            fill="url(#growthFillProjected)"
            connectNulls={false}
            isAnimationActive
            animationDuration={600}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
